/**
 * `RuntimeAction` and `ActionRejection` — what a parsed proposal becomes once
 * the engine has narrowed it (architecture.md §7.2 step 1, §3.3).
 *
 * @remarks
 * The parser proposes (`ParsedCommand`); the engine disposes. Narrowing turns
 * a proposal into exactly one of the slice's typed actions, or into a typed
 * rejection — never a throw (§3.3: player-facing failure is DATA, feeding the
 * in-world response system). Both unions discriminate on `type` and every
 * consumer narrows with `switch` + `never` guards, so a new verb or a new
 * refusal reason breaks compilation at every site that must care.
 *
 * Per the R2 handoff ("open vs use"), `open` is NOT an action: both the
 * `open` and `use` canonical intents narrow onto the single `use` action and
 * its use-interaction machinery. The design's "unlock/open are aliases of
 * use" lives here, one layer below vocabulary, exactly as recorded.
 *
 * @packageDocumentation
 */

import type { ActorId, DefaultResponseSlot, ExitId, ItemId, RoomId } from '@orpheus/content-schema';
import type { DirectionToken, EntityRef } from '@orpheus/parser-engine';

import type { ResolvedNarrativeLine } from '../narrative/conditional-text.js';

/**
 * A refused proposal: WHY (typed, for tests, tooling, and future
 * per-reason handling) plus WHAT THE PLAYER HEARS (engine-selected —
 * authored refusal narratives where content provides them, the generic
 * defaults where it stays silent). Rejections never consume a turn in v0
 * (architecture.md §3.3; the per-action override arrives with the action
 * definitions that need it).
 */
export interface ActionRejection {
	/** v0 policy: rejections never consume time. */
	readonly consumesTurn: false;
	/**
	 * The refusal the player reads — already selected (authored beats
	 * defaults) AND already RESOLVED (R15): each line's conditional reading
	 * was chosen at rejection creation, where the live state is, so this seam
	 * hands out exactly what the transcript will say. A consumer at any app
	 * edge may render these directly; none of them can ever receive an
	 * author's unresolved variants object (Calder's §5.2 — it broke four of
	 * his pins as a doc-comment problem, and is now unrepresentable instead).
	 */
	readonly narratives: readonly ResolvedNarrativeLine[];
	readonly reason: ActionRejectionReason;
	/**
	 * D58: slots the refusal spoke through, so the consumer that commits state
	 * (runClauses) can bump `session.responseUsage` — a refusal still consumes
	 * no turn and changes no WORLD state; usage is session metadata, exactly as
	 * it has been for parse teaches since D30.
	 */
	readonly spokenSlots?: readonly DefaultResponseSlot[];
}

/** Every way the v0 engine can refuse a parsed proposal — discriminated on `type`. */
export type ActionRejectionReason =
	/* D232: a verb other than speech aimed at an actor the player hears and cannot reach (`audibleFrom`). */
	| { readonly actorId: ActorId; readonly itemId: ItemId; readonly type: 'gift_condition_unmet' }
	/* D234: `attack` on a body that has already fallen. */
	| { readonly actorId: ActorId; readonly itemId: ItemId; readonly type: 'gift_refused' }
	/* D235: a thing offered to a body whose `giveInteractions` have an arm for it, none of whose conditions hold. */
	| { readonly actorId: ActorId; readonly type: 'actor_out_of_reach' }
	/* D235: a thing offered to a body with no `giveInteractions` arm for it — refused in the actor's words (`refusesGiftsNarratives`) or the `giftRefused` slot's. */
	| { readonly actorId: ActorId; readonly type: 'foe_defeated' }
	/* D234: `attack` on an actor with no `combat` block — refused in its words (`verbNarratives.attack`) or the `notAFoe` slot's. */
	| { readonly actorId: ActorId; readonly type: 'not_a_foe' }
	/* D232: an item verb aimed at an actor — refused in the actor's words (`verbNarratives`) or the `verbOnActor` slot's. */
	| { readonly actorId: ActorId; readonly type: 'order_unanswered'; readonly verbToken: string }
	/* D252: an order the body has no `orders` arm for, or none whose conditions hold. */
	| { readonly actorId: ActorId; readonly type: 'verb_on_actor'; readonly verbToken: string }
	| { readonly detail: string; readonly type: 'malformed_command' }
	| { readonly directionToken: DirectionToken; readonly type: 'no_exit_that_way' }
	| { readonly entityRef: EntityRef; readonly type: 'entity_not_visible' }
	| { readonly exitId: ExitId; readonly type: 'exit_leads_outward' }
	| { readonly exitId: ExitId; readonly type: 'exit_locked' }
	| { readonly instrumentItemId: ItemId; readonly targetItemId: ItemId; readonly type: 'no_matching_interaction' }
	// D245: no `targetItemId` when the thing was used on its own.
	| { readonly instrumentItemId: ItemId; readonly targetItemId?: ItemId; readonly type: 'condition_unmet' }
	| { readonly instrumentItemId: ItemId; readonly type: 'missing_target' }
	| { readonly intentToken: string; readonly itemId: ItemId; readonly type: 'missing_recipient' }
	/* D217: a verb other than looking, aimed at a SET under the pack's `set` policy — one at a time. */
	| { readonly intentToken: string; readonly type: 'plural_one_at_a_time' }
	| { readonly intentToken: string; readonly type: 'unknown_intent' }
	| { readonly intentToken: string; readonly type: 'unsupported_intent' }
	/* D135: `close X` refused in the item's own words (`closeRefusal`), its `when` holding. */
	| { readonly itemId: ItemId; readonly occupyingItemId: ItemId; readonly type: 'slot_occupied' }
	/* D135: the door is already in the position asked for — it did not move (D120's rule). */
	| { readonly itemId: ItemId; readonly type: 'already_equipped' }
	/* R25: a take refused because the hands are full — `drop` is the relief valve. */
	| { readonly itemId: ItemId; readonly type: 'already_held' }
	/* D135: `open X` refused in the item's own words (`openRefusal`), its `when` holding. */
	| { readonly itemId: ItemId; readonly type: 'capacity_exceeded' }
	| { readonly itemId: ItemId; readonly type: 'close_refused' }
	/* R25: dropping a thing the hands do not hold. */
	| { readonly itemId: ItemId; readonly type: 'door_unmoved' }
	/* D152: an act a SPENT thing refuses — its `durability.uses` all gone. */
	| { readonly itemId: ItemId; readonly type: 'hands_are_not_enough' }
	/* D235: `give <thing>` / `show <thing>` with nobody named, in a game where somebody takes gifts — parked as "to whom?"; `intentToken` is the verb whose binding joins the answer. */
	| { readonly itemId: ItemId; readonly type: 'item_spent' }
	/* D143: the thing is already worn, or is not worn, or cannot be, or its slot is taken. */
	| { readonly itemId: ItemId; readonly type: 'no_path_toward' }
	/* D234: `attack … with X` where the carried X declares no `weapon`. */
	| { readonly itemId: ItemId; readonly type: 'not_a_weapon' }
	| { readonly itemId: ItemId; readonly type: 'not_carried' }
	| { readonly itemId: ItemId; readonly type: 'not_equippable' }
	| { readonly itemId: ItemId; readonly type: 'not_equipped' }
	/** D242: `take <thing> from <place>` where the thing is not in that place. */
	| { readonly itemId: ItemId; readonly type: 'not_in_place_named' }
	/** D50: `close the shelving` — it holds things, but it has no door to shut. */
	| { readonly itemId: ItemId; readonly type: 'not_openable' }
	/* D65: a latched switch, thrown, asked to come back. */
	| { readonly itemId: ItemId; readonly type: 'not_portable' }
	/* R29: seen, not reached — `reachableWhen` unmet on the item or a container above it. */
	| { readonly itemId: ItemId; readonly type: 'open_refused' }
	/* D63: bare hands offered as the instrument, and hands are not enough. */
	| { readonly itemId: ItemId; readonly type: 'search_while_shut' }
	/* D63: a verb aimed at the player's own hands. */
	| { readonly itemId: ItemId; readonly type: 'stock_empty' }
	/* D120: the switch is already in the position asked for — it did not move. */
	| { readonly itemId: ItemId; readonly type: 'stock_full' }
	/* D244: `take` from a stock whose draw has given everything it counts. */
	| { readonly itemId: ItemId; readonly type: 'switch_latched' }
	/* D260: a switch held where it is by its own `turnOnRefusal` / `turnOffRefusal`, its `when` holding. */
	| { readonly itemId: ItemId; readonly type: 'switch_refused' }
	/* D244: `take` from a stock when what it gives would pass its counter's ceiling — nothing moves. */
	| { readonly itemId: ItemId; readonly type: 'switch_unmoved' }
	/* D199: `take X` refused in the item's own words (`takeRefusal`), its `when` holding. */
	| { readonly itemId: ItemId; readonly type: 'take_refused' }
	/* D53: `search` aimed through a door that is still shut. */
	| { readonly itemId: ItemId; readonly type: 'unreachable' }
	/* D196: an authored verb aimed at a thing with no arm for it, or none whose conditions hold. */
	| { readonly itemId: ItemId; readonly type: 'verb_unanswered'; readonly verbToken: string }
	| { readonly targetItemId: ItemId; readonly type: 'missing_instrument' }
	| { readonly targetItemId: ItemId; readonly type: 'no_interaction_possible' }
	/** D39: nothing the pack authors acts on this item — asking "with what?" would be a trap. */
	| { readonly type: 'body_aside' }
	| { readonly type: 'missing_direction' };

/** D251: what an authored verb is done to — a thing, or a body, and never both. */
export type ActedOn = { readonly actorId: ActorId; readonly itemId?: never } | { readonly actorId?: never; readonly itemId: ItemId };

/**
 * One party to a question (D231): a thing, the room the player stands in,
 * or (D232) an actor — the three kinds of entity that may be addressed and
 * may be asked about. An actor addressed answers from its own table and its
 * greeting, never from the subject's dossier line (that is another voice).
 */
export type AskParty =
	| { readonly actorId: ActorId; readonly kind: 'actor' }
	| { readonly itemId: ItemId; readonly kind: 'item' }
	| { readonly kind: 'room'; readonly roomId: RoomId };

/** Who is asked — see {@link RuntimeAction}'s `ask` arm. */
export type AskAddressee = AskParty;

/**
 * What is asked about (D231): a party, with the word the player used for it
 * when the parser kept one (D226, E20 — the surface is what a topic's plain
 * keys are matched against), or a bare topic word that names no entity.
 */
export type AskSubject = { readonly kind: 'word'; readonly word: string } | ({ readonly surface?: string } & AskParty);

/**
 * One narrowed, engine-legal action for the v0 slice — discriminated on
 * `type`. `ask` is the only action whose object is optional: a bare `ask`
 * reaches EURYDICE and earns her generic deflection rather than a rejection
 * (it parsed, it is legal, she declines).
 */
export type RuntimeAction =
	| {
		/** D31: exactly one of the two is present — a bearing, or a named destination resolved through exit links. */
		readonly destinationItemId?: ItemId;
		readonly directionToken?: DirectionToken;
		readonly type: 'go';
	}
	/*
	 * D234: the STRIKE — `attack <actor> [with <item>]`. The actor intercept
	 * mints it for a body with a `combat` block (ruling 3: attackable because
	 * the loader proved its hostility can come true); `weaponItemId` is the
	 * thing named after `with`, judged by the handler's ladder, and absent
	 * means the best thing in hand, else bare hands.
	 */
	| {
		readonly actorId: ActorId;
		readonly directObjectItemId?: ItemId;
		readonly indirectObjectItemId?: ItemId;
		readonly toolItemId?: ItemId;
		readonly type: 'order';
		readonly verbToken: string;
	}
	/*
	 * D235: a thing HANDED to a body (`give`, `hand`, `offer` … `to`), or
	 * SHOWN to one (`show … to`) — one pair, one handler, one authored table
	 * (`giveInteractions`): the two differ only in whether the body may keep
	 * the thing (`consumesItem` counts for `give` and never for `show`). The
	 * actor intercept mints them for a body in the recipient's slot; the
	 * roster's own arm answers the sentence with nobody, or a thing, there.
	 */
	| { readonly actorId: ActorId; readonly itemId: ItemId; readonly type: 'give' }
	| { readonly actorId: ActorId; readonly itemId: ItemId; readonly type: 'show' }
	/*
	 * D232: looking at an ACTOR — the environment's line, the player seeing
	 * them, through the same describer items and rooms read. Its own action
	 * rather than `inspect` with a wider id, because every other reader of
	 * `inspect` requires an item (the log's read mark, the reach gate's
	 * containment walk) and an actor is not one.
	 */
	| { readonly actorId: ActorId; readonly type: 'attack'; readonly weaponItemId?: ItemId }
	/*
	 * D240: going through what a BODY carries — `handleSearch`'s act on an
	 * actor, its own action for `inspect_actor`'s reason: every reader of
	 * `search` requires an item (the shut-door refusal, the durability
	 * seam), and a body is not one.
	 */
	| { readonly actorId: ActorId; readonly type: 'inspect_actor' }
	/*
	 * D252: an ORDER — a clause said to a body (`tell TALLY to follow me`):
	 * `verbToken` is the canonical verb it was told, the key its `orders`
	 * table is read with, and the clause's things ride along so the arms read
	 * the sentence (the body itself names no item, D232).
	 */
	| { readonly actorId: ActorId; readonly type: 'search_actor' }
	| { readonly addressee?: AskAddressee; readonly subject?: AskSubject; readonly type: 'ask' }
	/*
	 * D196: an AUTHORED verb — one the pack declared and the roster does not
	 * know — applied to one thing. `verbToken` is the canonical token, the key
	 * the item's `verbInteractions` table is read with; the one-object general
	 * case that `use` is the two-object sugar for. D229 (E22): the sentence's
	 * other two slots ride along when they named a thing, so the arms can read
	 * what the act was done WITH and TO — `cut the tree with the pipe` carries
	 * the pipe, and an arm that asks `tool_is` can refuse it. D251: the one it
	 * is done to may be a BODY — `release tally` reads TALLY's own arms — and
	 * it is one or the other, never both.
	 */
	// D269: `isInstrumentImplied` — the player named only the target, and carried one thing that opens it.
	| { readonly instrumentItemId: ItemId; readonly isInstrumentImplied?: true; readonly targetItemId?: ItemId; readonly type: 'use' }
	/*
	 * D50: the three container verbs. `search` is its own action rather than an
	 * `inspect` alias because searching a thing means going through it, and a
	 * verb that returns the description looking already gave is decorative.
	 * `open_container` / `close_container` are the bare-hands door verbs — the
	 * first legitimate no-instrument `open` the engine has had, which also
	 * retires the audit's §1 as a by-product.
	 */
	// D245: no `targetItemId` is the thing used on its own — narrowed only when one of its target-free arms holds.
	| { readonly itemId: ItemId; readonly type: 'close_container' }
	| { readonly itemId: ItemId; readonly type: 'drop' }
	| { readonly itemId: ItemId; readonly type: 'equip' }
	| { readonly itemId: ItemId; readonly type: 'inspect' }
	/*
	 * D217: looking at a SET — the things a plural named, answered in one line
	 * under the pack's `set` policy: their group's description, or their names.
	 */
	| { readonly itemId: ItemId; readonly type: 'open_container' }
	/*
	 * D62: casing the ROOM — Mike's ruling. Searching where you stand is a real
	 * act now, not an alias of looking: it surveys what would reward going
	 * through properly, and reveals nothing itself, so the per-thing
	 * discoveries (D50) stay discoveries.
	 */
	| { readonly itemId: ItemId; readonly type: 'search' }
	/*
	 * D56: the switch verbs, structurally identical to the door pair above and
	 * handled by the same transition. `turn` stays declared-unsupported in the
	 * vocabulary and is mechanized per-ITEM: only a thing declaring `switchable`
	 * narrows to these, so everything else keeps earning its authored D47
	 * refusal.
	 */
	/** D143: wear or wield a carried thing into its slot; take it off again. */
	| { readonly itemId: ItemId; readonly type: 'switch_off' }
	| { readonly itemId: ItemId; readonly type: 'switch_on' }
	| { readonly itemId: ItemId; readonly type: 'take' }
	| { readonly itemId: ItemId; readonly type: 'unequip' }
	| { readonly itemIds: readonly ItemId[]; readonly type: 'inspect_set' }
	/*
	 * D231: a question has two parts, both optional. The ADDRESSEE is who is
	 * asked — the direct object (`ask the voice about X`, `talk to the voice`)
	 * — and its topic table answers first. The SUBJECT is what is asked about
	 * — the `about` phrase: a thing (present, or a topic the player has met,
	 * D226), the room (D137's `ask about the corridor`, once its own
	 * `ask_room` action, folded in here when the room became one party of two
	 * — a discriminated party, never two optionals, so "both" stays
	 * unrepresentable), or a bare WORD some present thing's topics are keyed
	 * on (`ask about the directive`). Neither: a bare `ask`, the deflection.
	 */
	| { readonly type: 'help' }
	| { readonly type: 'inventory' }
	| { readonly type: 'look' }
	| { readonly type: 'map' }
	| { readonly type: 'search_room' }
	| { readonly type: 'wait' }
	/**
	 * D52: letting time pass on purpose. Object-free, consumes a turn, changes
	 * nothing else — the cheapest verb in the roster and the one a player
	 * reaches for the moment a clock is mentioned. Load-bearing since R9's
	 * scheduler: waiting is how a player chooses to let a countdown run.
	 */
	| ({
		readonly indirectObjectItemId?: ItemId;
		readonly toolItemId?: ItemId;
		readonly type: 'act';
		readonly verbToken: string;
	} & ActedOn);

/** How narrowing one `ParsedCommand` ended: an action to validate and run, or a typed refusal. */
export type CommandNarrowingOutcome =
	| { readonly action: RuntimeAction; readonly type: 'narrowed' }
	| { readonly rejection: ActionRejection; readonly type: 'rejected' };

/**
 * Every `ActionRejectionReason` discriminant, as a value (R12) — the
 * occurrence-matcher validation checks authored `command_rejected.reason`s
 * against this. Built from a `Record satisfies` so the compiler enforces
 * BOTH directions: a reason missing from the record and a key that is not a
 * reason are each a type error at this site.
 */
const REJECTION_REASON_RECORD = {
	actor_out_of_reach: true,
	already_equipped: true,
	already_held: true,
	body_aside: true,
	capacity_exceeded: true,
	close_refused: true,
	condition_unmet: true,
	door_unmoved: true,
	entity_not_visible: true,
	exit_leads_outward: true,
	exit_locked: true,
	foe_defeated: true,
	gift_condition_unmet: true,
	gift_refused: true,
	hands_are_not_enough: true,
	item_spent: true,
	malformed_command: true,
	missing_direction: true,
	missing_instrument: true,
	missing_recipient: true,
	missing_target: true,
	no_exit_that_way: true,
	no_interaction_possible: true,
	no_matching_interaction: true,
	no_path_toward: true,
	not_a_foe: true,
	not_a_weapon: true,
	not_carried: true,
	not_equippable: true,
	not_equipped: true,
	not_in_place_named: true,
	not_openable: true,
	not_portable: true,
	open_refused: true,
	order_unanswered: true,
	plural_one_at_a_time: true,
	search_while_shut: true,
	slot_occupied: true,
	stock_empty: true,
	stock_full: true,
	switch_latched: true,
	switch_refused: true,
	switch_unmoved: true,
	take_refused: true,
	unknown_intent: true,
	unreachable: true,
	unsupported_intent: true,
	verb_on_actor: true,
	verb_unanswered: true,
} as const satisfies Record<ActionRejectionReason['type'], true>;

/** Every rejection reason, in declaration (sorted) order. */
export const ACTION_REJECTION_REASON_TYPES = Object.keys(REJECTION_REASON_RECORD) as readonly ActionRejectionReason['type'][];

/**
 * Every `RuntimeAction` discriminant, as a value (R12) — the
 * occurrence-matcher validation checks authored `command_executed.intent`s
 * against this (the engine's action vocabulary, not the surface verb: `turn
 * on the kettle` executes as `switch_on`). Same two-way compiler enforcement
 * as the rejection list.
 */
const RUNTIME_ACTION_RECORD = {
	act: true,
	ask: true,
	attack: true,
	close_container: true,
	drop: true,
	equip: true,
	give: true,
	go: true,
	help: true,
	inspect: true,
	inspect_actor: true,
	inspect_set: true,
	inventory: true,
	look: true,
	map: true,
	open_container: true,
	order: true,
	search: true,
	search_actor: true,
	search_room: true,
	show: true,
	switch_off: true,
	switch_on: true,
	take: true,
	unequip: true,
	use: true,
	wait: true,
} as const satisfies Record<RuntimeAction['type'], true>;

/** Every runtime action type, in declaration (sorted) order. */
export const RUNTIME_ACTION_TYPES = Object.keys(RUNTIME_ACTION_RECORD) as readonly RuntimeAction['type'][];
